import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Input, InputField } from "@repo/components/src/ui/input";
import { AlertCircleIcon } from "lucide-react-native";
import { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

type InputFieldProps = ComponentProps<typeof InputField>;
type InputProps = ComponentProps<typeof Input>;

export type FormInputTextProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, string | undefined | null> & {
    keyboardType: InputFieldProps["keyboardType"];
    textContentType: InputFieldProps["textContentType"];
    autoCapitalize: InputFieldProps["autoCapitalize"];
    inputProps?: InputProps;
    labelProps?: ComponentProps<typeof FormControlLabelText>;
    onBlur?: () => void;
    onChangeText?: (text: string) => void;
    onSubmitEditing?: () => void;
    returnKeyType?: InputFieldProps["returnKeyType"];
    maxLength?: InputFieldProps["maxLength"];
  };

export default function FormInputText<TFieldValues extends FieldValues>(
  props: FormInputTextProps<TFieldValues>,
) {
  const {
    control,
    name,
    placeholder,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    keyboardType,
    textContentType,
    autoCapitalize,
    labelSize = "md",
    labelProps = {},
    inputProps = {},
    onBlur: onBlurProp,
    onChangeText: onChangeTextProp,
    onSubmitEditing,
    returnKeyType,
    maxLength,
  } = props;
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabledProp}
      render={({
        field: { onChange, value, ref, onBlur, disabled },
        fieldState: { error },
      }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
        >
          {!!label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize} {...labelProps}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <Input
            {...inputProps}
            isDisabled={disabled}
            isInvalid={!!error}
            size={size}
          >
            <InputField
              ref={focusScrollRef(ref)}
              onBlur={() => {
                onBlur();
                onBlurProp?.();
              }}
              placeholder={placeholder}
              value={value || ""}
              onChangeText={(text) => {
                onChange(text);
                onChangeTextProp?.(text);
              }}
              onSubmitEditing={onSubmitEditing}
              returnKeyType={returnKeyType}
              keyboardType={keyboardType}
              maxLength={maxLength}
              textContentType={textContentType}
              autoCapitalize={autoCapitalize}
            />
          </Input>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
